import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './EditEmployeeModal.css';
import ReactDOM from 'react-dom';

// function to handle the edit of rows AND the creation of new employees
function EditEmployeeModal({ employee, onClose, onSave, isNew = false, isReactivating = false }) {

  //Boolean to make sure that the button "Guardar" is not pressed more than once. 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // New state for the create user toggle
  const [createUserAccount, setCreateUserAccount] = useState(false);

  const [formData, setFormData] = useState({
    full_name: employee?.full_name || '',
    wsp_email: employee?.wsp_email || '@wsp.com.mx',
    date_of_entry: employee?.date_of_entry || '',
    remaining_days: employee?.remaining_days || 0,
    renovation_day: employee?.renovation_day || '',
    area: employee?.area || '',
    active: true
  });

  // List of available areas / departments / "gerencias"
  const areas = [
    "Administrativo / Dirección",
    "Fábrica ABAP",
    "Staffing",
    "RH",
    "PMO-IT",
    "Finanzas",
    "Ventas",
    "Comercial",
    "Otros"
  ];

  // vacation days calculation function
  const calculateVacationDays = (dateOfEntry) => {
    if (!dateOfEntry) return 0;

    const today = new Date();
    const entryDate = new Date(dateOfEntry);

    let years = today.getFullYear() - entryDate.getFullYear();

    if (
      today.getMonth() < entryDate.getMonth() ||
      (today.getMonth() === entryDate.getMonth() && today.getDate() < entryDate.getDate())
    ) {
      years--;
    }

    let days;
    if (years <= 0) return 0;
    else if (years === 1) days = 12;
    else if (years === 2) days = 14;
    else if (years === 3) days = 16;
    else if (years === 4) days = 18;
    else if (years === 5) days = 20;
    else if (years >= 6 && years <= 10) days = 22;
    else if (years >= 11 && years <= 15) days = 24;
    else if (years >= 16 && years <= 20) days = 26;
    else if (years >= 21 && years <= 25) days = 28;
    else if (years >= 26 && years <= 30) days = 30;
    else if (years >= 31) days = 32;

    return days;
  };

  // Calculate next renovation date based on entry date
  const calculateRenovationDate = (entryDate) => {
    if (!entryDate) return '';

    // Parse the entry date string
    const entryParts = entryDate.split('-');
    const entryYear = parseInt(entryParts[0]);
    const entryMonth = parseInt(entryParts[1]) - 1;
    const entryDay = parseInt(entryParts[2]);

    // Create dates using components to avoid timezone issues
    const entry = new Date(entryYear, entryMonth, entryDay, 12, 0, 0);
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    // Create the renovation date with the same day/month as entry date
    const nextRenovation = new Date(today.getFullYear(), entryMonth, entryDay, 12, 0, 0);

    // If we've already passed this year's date, add a year
    if (today > nextRenovation) {
      nextRenovation.setFullYear(today.getFullYear() + 1);
    }

    // Format date properly as YYYY-MM-DD
    const nextYear = nextRenovation.getFullYear();
    const nextMonth = (nextRenovation.getMonth() + 1).toString().padStart(2, '0');
    const nextDay = nextRenovation.getDate().toString().padStart(2, '0');

    return `${nextYear}-${nextMonth}-${nextDay}`;
  };

  const handleDateChange = (date, fieldName) => {
    if (date) {
      // Check if selected date is a leap day
      if (date.getMonth() === 1 && date.getDate() === 29) {
        setError('No se pueden seleccionar días bisiestos (29 de febrero). Por favor, seleccione el 1 de marzo.');
        // Optionally, automatically set to March 1st of the same year
        const march1st = new Date(date.getFullYear(), 2, 1);
        date = march1st;
      } else {
        setError(''); // Clear error if date is valid
      }

      // Fix: Adjust the date to maintain the correct day regardless of timezone
      const adjustedDate = new Date(date);
      adjustedDate.setHours(12); // Set to noon to avoid timezone issues
      const formattedDate = adjustedDate.toISOString().split('T')[0];

      setFormData((prev) => ({ ...prev, [fieldName]: formattedDate }));

      if (fieldName === 'date_of_entry') {
        const vacationDays = calculateVacationDays(formattedDate);

        // Calculate renovation date with the same timezone fix
        const renovationDate = calculateRenovationDate(formattedDate);

        setFormData((prev) => ({
          ...prev,
          renovation_day: renovationDate,
          // Only update remaining_days if it's empty or 0
          ...(prev.remaining_days === 0 || prev.remaining_days === '' ? { remaining_days: vacationDays } : {})
        }));
      }
    }
  };

  // Update the handleChange function to handle area changes specifically
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // If changing area to "Administrativo / Dirección", disable user creation
    if (name === 'area' && value === 'Administrativo / Dirección') {
      setCreateUserAccount(false);
    }
    
    setError(''); // Clear error when user starts typing
  };

  //Helper function to checkfor Leap day
  const isLeapDay = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date.getMonth() === 1 && date.getDate() === 29; // Month is 0-based, so 1 is February
  };

  const isPastDay = (date) => {
    // Return true if the date is in the past (before today)
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison
    return date < today;
  };

  //Validate all parameters before saving
  const validateForm = () => {
    // Check for empty fields
    if (!formData.full_name.trim()) {
      setError('El campo "Nombre completo" no puede estar vacío.');
      return false;
    }

    if (!formData.wsp_email.trim()) {
      setError('El campo "Correo WSP" no puede estar vacío.');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.wsp_email)) {
      setError('Por favor, ingrese un correo electrónico válido.');
      return false;
    }

    if (!formData.date_of_entry) {
      setError('El campo "Fecha de ingreso" no puede estar vacío.');
      return false;
    }

    if (formData.remaining_days === '' || formData.remaining_days === null) {
      setError('El campo "Días disponibles" no puede estar vacío.');
      return false;
    }

    if (!formData.renovation_day) {
      setError('El campo "Renovación" no puede estar vacío.');
      return false;
    }

    if (!formData.area) {
      setError('El campo "Área" no puede estar vacío.');
      return false;
    }

    // Check for leap days
    if (isLeapDay(formData.date_of_entry)) {
      setError('No se pueden seleccionar días bisiestos (29 de febrero) en Fecha de ingreso. Por favor, seleccione el 1 de marzo.');
      return false;
    }

    if (isLeapDay(formData.renovation_day)) {
      setError('No se pueden seleccionar días bisiestos (29 de febrero) en Renovación. Por favor, seleccione el 1 de marzo.');
      return false;
    }

    // All validations passed
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear any previous error

    // Run all validations
    if (!validateForm()) {
      return; // Stop if validation fails
    }

    setIsSubmitting(true);
    try {
      // Pass the createUserAccount flag along with the form data
      await onSave(formData, createUserAccount);
    } catch (error) {
      console.error('Error saving employee data:', error);
      setError('Error al guardar los datos. Por favor, intente nuevamente.');
      setIsSubmitting(false);
    }
  };



  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{isNew ? 'Nuevo Empleado' : isReactivating ? 'Reactivar Empleado' : 'Editar Empleado'}</h2>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre completo:</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Correo WSP:</label>
            <input
              type="email"
              name="wsp_email"
              value={formData.wsp_email}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Área:</label>
            <select
              name="area"
              value={formData.area}
              onChange={handleChange}
              required
            >
              <option value="" disabled>Seleccionar área</option>
              {areas.map((area, index) => (
                <option key={index} value={area}>
                  {area}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Fecha de ingreso:</label>
            <DatePicker
              selected={formData.date_of_entry ? new Date(formData.date_of_entry + 'T12:00:00') : null}
              onChange={(date) => handleDateChange(date, 'date_of_entry')}
              dateFormat="dd/MM/yyyy"
              filterDate={() => true}
              calendarClassName="weekend-enabled"
              popperPlacement="bottom"
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                },
              }}
              portalId="root-portal" // This enables portal mode
            />
          </div>

          <div className="form-group">
            <label>Días disponibles:</label>
            <input
              type="number"
              name="remaining_days"
              value={formData.remaining_days}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Renovación:</label>
            <DatePicker
              selected={formData.renovation_day ? new Date(formData.renovation_day + 'T12:00:00') : null}
              onChange={(date) => handleDateChange(date, 'renovation_day')}
              dateFormat="dd/MM/yyyy"
              filterDate={(date) => {
                return !isPastDay(date);
              }}
              calendarClassName="weekend-enabled"
              popperPlacement="bottom"
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                },
              }}
              portalId="root-portal" // This enables portal mode
            />
          </div>

          {/* Show the toggle for both new employee creation and reactivation */}
          {(isNew || isReactivating) && (
            <div className="form-group checkbox-group">
              <label>¿Crear cuenta de usuario para este empleado?</label>
              <input
                type="checkbox"
                checked={createUserAccount}
                onChange={(e) => setCreateUserAccount(e.target.checked)}
                disabled={formData.area === 'Administrativo / Dirección'}
                className="checkbox-input"
              />
              {formData.area === 'Administrativo / Dirección' && (
                <div className="manager-warning">
                  El empleado que está a punto de registrar en base de datos es un gerente, por lo que no se puede crear una cuenta de usuario desde aquí. Por favor, una vez termine de registrarlo en esta ventana, cree el usuario de este gerente desde "Administración de gerencias" usando la cuenta administradora.
                </div>
              )}
            </div>
          )}

          <div className="modal-buttons">
            <button type="submit" className="save-button" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : isReactivating ? 'Guardar y Reactivar' : 'Guardar'}
            </button>
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditEmployeeModal;